import { useRef, useState } from "react"
import { useRouter } from "next/router"

import {
  useClerk,
  useOrganization,
  useOrganizationList,
  useUser,
} from "@clerk/nextjs"
import type { SessionResource } from "@clerk/nextjs/types"
import { Avatar } from "@coval/ui/avatar"
import { Button } from "@coval/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@coval/ui/card"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@coval/ui/dialog"
import { Input } from "@coval/ui/input"
import { Label } from "@coval/ui/label"
import { toast } from "@coval/ui/toast"
import { AlertTriangle } from "lucide-react"

import {
  ClerkActionProvider,
  useClerkActions,
} from "@/components/auth/ClerkActionProvider"
import { OrganizationPermissionNotice } from "@/components/auth/OrganizationPermissionNotice"
import { ProfileEditor } from "@/components/auth/ProfileEditor"
import { useSyncActiveOrganization } from "@/hooks/useSyncActiveOrganization"
import { getRouteOrganizationSlug } from "@/utils/workspaceRoutes"

import { useInvalidateOrganizationUsers } from "../transport"
import { UploadedAudioSettings } from "./UploadedAudioSettings"

export function OrganizationGeneralSettings({
  showManagement = true,
  showUploadedAudio = false,
}: {
  showManagement?: boolean
  showUploadedAudio?: boolean
}) {
  const { organization, isLoaded } = useOrganization()
  const { user } = useUser()
  const router = useRouter()
  const slug = getRouteOrganizationSlug(router.asPath)
  if (!isLoaded || !organization || !slug || organization.slug !== slug) {
    return (
      <p className="text-sm text-coval-text-muted">Loading organization…</p>
    )
  }
  return (
    <ClerkActionProvider key={`${organization.id}:${slug}:${user?.id}`}>
      <OrganizationGeneralContent
        showManagement={showManagement}
        showUploadedAudio={showUploadedAudio}
      />
    </ClerkActionProvider>
  )
}
function OrganizationGeneralContent({
  showManagement,
  showUploadedAudio,
}: {
  showManagement: boolean
  showUploadedAudio: boolean
}) {
  const clerk = useClerk()
  const { organization, membership } = useOrganization()
  const { user } = useUser()
  const { setActive, userMemberships } = useOrganizationList({
    userMemberships: { pageSize: 10 },
  })
  const { busy, run } = useClerkActions()
  const router = useRouter()
  const { navigateAfterOrgSwitch } = useSyncActiveOrganization()
  const invalidateUsers = useInvalidateOrganizationUsers()
  const [editing, setEditing] = useState(false)
  const [refreshError, setRefreshError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [removalPending, setRemovalPending] = useState(false)
  const [removalError, setRemovalError] = useState<string | null>(null)
  const continuationLock = useRef(false)
  const [continuing, setContinuing] = useState(false)
  const [action, setAction] = useState<"leave" | "delete" | null>(null)
  const [confirmation, setConfirmation] = useState("")
  if (!organization || !membership || !user) {
    return null
  }
  const scoped =
    membership.organization.id === organization.id &&
    getRouteOrganizationSlug(router.asPath) === organization.slug
  const canManage =
    scoped && membership.permissions.includes("org:sys_profile:manage")
  if (!showManagement) {
    return showUploadedAudio ? (
      <UploadedAudioSettings canManage={canManage} />
    ) : null
  }
  const canDelete =
    scoped &&
    organization.adminDeleteEnabled &&
    membership.permissions.includes("org:sys_profile:delete")
  const orgId = organization.id
  const assertScope = () => {
    if (
      clerk.organization?.id !== orgId ||
      clerk.user?.id !== user.id ||
      getRouteOrganizationSlug(router.asPath) !== organization.slug
    ) {
      throw new Error(
        "Organization changed. Reopen Settings before continuing.",
      )
    }
  }
  const refreshProfile = async (message: string) => {
    setRefreshing(true)
    const results = await Promise.allSettled([
      Promise.resolve().then(() => userMemberships?.revalidate?.()),
      Promise.resolve().then(invalidateUsers),
    ])
    setRefreshError(
      results.some((result) => result.status === "rejected") ? message : null,
    )
    setRefreshing(false)
  }
  const moveAfterRemoval = async () => {
    const sessionId = clerk.session?.id
    const previousPath = router.asPath
    const sameAccountRoute = () =>
      clerk.user?.id === user.id &&
      getRouteOrganizationSlug(router.asPath) === organization.slug
    if (continuationLock.current || !sameAccountRoute()) {
      return
    }
    continuationLock.current = true
    let navigationSession: SessionResource | undefined
    setContinuing(true)
    setRemovalError(null)
    try {
      let next: typeof organization | undefined
      try {
        const remaining = await user.getOrganizationMemberships({ pageSize: 1 })
        next = remaining.data[0]?.organization
      } catch {
        if (sameAccountRoute()) {
          setRemovalError(
            "Organization access changed, but remaining memberships could not load. Retry continuing; the removal will not be repeated.",
          )
        }
        return
      }
      if (!sameAccountRoute()) {
        return
      }
      try {
        try {
          await userMemberships?.revalidate?.()
        } catch {
          /* Switching reloads the membership list on the destination. */
        }
        if (!sameAccountRoute()) {
          return
        }
        if (setActive) {
          await setActive({
            organization: next?.id ?? null,
            navigate: async ({ session, decorateUrl }) => {
              if (
                session.user?.id !== user.id ||
                session.id !== sessionId ||
                session.lastActiveOrganizationId !== (next?.id ?? null) ||
                router.asPath !== previousPath
              ) {
                if (
                  session.user?.id === user.id &&
                  getRouteOrganizationSlug(router.asPath) === organization.slug
                ) {
                  setRemovalError(
                    "Organization access changed, but switching organizations failed. Retry continuing; the removal will not be repeated.",
                  )
                }
                return
              }
              navigationSession = session
              await navigateAfterOrgSwitch(next ?? null, decorateUrl)
            },
          })
        }
      } catch (error) {
        if (
          setActive &&
          navigationSession &&
          navigationSession.user?.id === user.id &&
          navigationSession.id === sessionId &&
          navigationSession.lastActiveOrganizationId === (next?.id ?? null) &&
          (clerk.session === undefined || clerk.session?.id === sessionId) &&
          clerk.client?.lastActiveSessionId === sessionId &&
          router.asPath === previousPath
        ) {
          // The removed organization cannot be restored, so release Clerk's transitive state into the remaining account and preserve a retry.
          try {
            await setActive({
              session: sessionId,
              organization: next?.id ?? null,
            })
          } catch {
            toast.error({
              error,
              title:
                "Could not restore your account. Reload this page to continue.",
            })
            return
          }
          if (sameAccountRoute()) {
            toast.error({
              error,
              title: "Organization access changed, but navigation failed.",
              duration: Number.POSITIVE_INFINITY,
              action: {
                label: "Retry continuing",
                onClick: () => void moveAfterRemoval(),
              },
            })
          }
        }
        if (sameAccountRoute()) {
          setRemovalError(
            "Organization access changed, but switching organizations failed. Retry continuing; the removal will not be repeated.",
          )
        }
      }
    } finally {
      continuationLock.current = false
      setContinuing(false)
    }
  }
  const remove = async () => {
    if (
      !action ||
      confirmation !== organization.name ||
      busy ||
      removalPending ||
      !scoped ||
      !setActive ||
      (action === "delete" && !canDelete)
    ) {
      return
    }
    const outcome = await run(
      null,
      async () => {
        assertScope()
        if (action === "leave" && membership.role === "org:admin") {
          const admins = await organization.getMemberships({
            role: ["org:admin"],
            pageSize: 1,
          })
          assertScope()
          if (admins.total_count <= 1) {
            throw new Error(
              "Promote another member to Admin before leaving this organization.",
            )
          }
        }
        if (action === "delete") {
          await organization.destroy()
        } else {
          await membership.destroy()
        }
        setRemovalPending(true)
        await moveAfterRemoval()
      },
      orgId,
      true,
    )
    if (outcome) {
      setAction(null)
      setConfirmation("")
    }
  }
  if (removalPending) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Organization access updated</CardTitle>
          <CardDescription>
            The change is saved. Continue to your account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {removalError ? (
            <>
              <p role="alert" className="text-sm text-coval-warning-text">
                {removalError}
              </p>
              <Button
                variant="secondary"
                disabled={busy || continuing || !setActive}
                onClick={() => void moveAfterRemoval()}
              >
                Retry continuing
              </Button>
            </>
          ) : (
            <p role="status" className="text-sm text-coval-text-muted">
              Continuing to your account…
            </p>
          )}
        </CardContent>
      </Card>
    )
  }
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle
            className={
              canManage ? "text-base" : "text-base text-coval-text-muted"
            }
          >
            Organization profile
          </CardTitle>
          <CardDescription>
            The name and logo your team sees across Coval.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {refreshError && (
            <div className="flex items-center gap-3">
              <p role="alert" className="text-sm text-coval-warning-text">
                {refreshError}
              </p>
              <Button
                variant="secondary"
                disabled={busy || refreshing}
                onClick={() => void refreshProfile(refreshError)}
              >
                Retry
              </Button>
            </div>
          )}
          {editing ? (
            <ProfileEditor
              name={organization.name}
              imageUrl={organization.imageUrl}
              hasImage={organization.hasImage}
              imageLabel="Logo"
              disabled={!canManage || !!refreshError || refreshing}
              fields={[
                { name: "name", label: "Name", value: organization.name },
                {
                  name: "slug",
                  label: "Slug",
                  value: organization.slug ?? "",
                  readOnly: true,
                  optional: true,
                },
              ]}
              onCancel={() => setEditing(false)}
              onSave={async (values, image) => {
                if (
                  !canManage ||
                  refreshError ||
                  refreshing ||
                  removalPending
                ) {
                  return false
                }
                const result = await run(
                  "Organization profile updated",
                  async (assertCurrent) => {
                    assertScope()
                    let saved = false
                    try {
                      if (image !== undefined) {
                        await organization.setLogo({ file: image })
                        assertCurrent()
                        assertScope()
                      }
                      if (values.name.trim() !== organization.name) {
                        await organization.update({ name: values.name.trim() })
                      }
                      saved = true
                    } finally {
                      await refreshProfile(
                        saved
                          ? "Change saved, but profile data could not refresh. Retry loading before making another change."
                          : "Profile data could not refresh. Retry loading before making another change.",
                      )
                    }
                  },
                  orgId,
                )
                return !!result
              }}
            />
          ) : (
            <div className="flex flex-wrap items-center gap-4">
              <Avatar
                name={organization.name}
                src={organization.imageUrl}
                size="lg"
              />
              <div className="flex-1">
                <p className="text-sm font-medium">{organization.name}</p>
                <p className="text-xs text-coval-text-muted">
                  {organization.slug}
                </p>
              </div>
              <Button
                variant="secondary"
                disabled={busy || !canManage || !!refreshError || refreshing}
                onClick={() => setEditing(true)}
              >
                Update profile
              </Button>
            </div>
          )}
          {!canManage && (
            <OrganizationPermissionNotice
              reason="Your role can view the organization profile. An admin can change its name and logo."
              request="updating the organization profile"
              disabled={busy}
            />
          )}
        </CardContent>
      </Card>
      {showUploadedAudio && <UploadedAudioSettings canManage={canManage} />}
      {organization.selfServeSSOEnabled &&
        scoped &&
        membership.permissions.includes("org:sys_entconns:manage") && (
          <Card>
            <CardHeader>
              <CardTitle>Single sign-on</CardTitle>
              <CardDescription>
                Manage your organization’s identity provider and directory sync.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="secondary"
                disabled={busy || refreshing || removalPending}
                onClick={() => {
                  if (
                    clerk.organization?.id === orgId &&
                    clerk.user?.id === user.id &&
                    getRouteOrganizationSlug(router.asPath) ===
                      organization.slug
                  ) {
                    clerk.openOrganizationProfile({
                      __experimental_startPath: "/organization-security",
                    })
                  }
                }}
              >
                Manage single sign-on
              </Button>
            </CardContent>
          </Card>
        )}
      <Card>
        <CardHeader>
          <CardTitle className="text-base text-coval-danger-text">
            Organization access
          </CardTitle>
          <CardDescription>
            Changes here affect your access to this organization.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium">Leave organization</p>
              <p className="mt-1 text-sm text-coval-text-muted">
                You’ll lose access to this organization and its workspaces. An
                admin will need to invite you back.
              </p>
            </div>
            <Button
              variant="danger"
              disabled={busy || !scoped || !setActive}
              onClick={() => {
                setAction("leave")
                setConfirmation("")
              }}
            >
              Leave organization
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-4 border-t border-coval-border pt-6">
            <div className="flex-1">
              <p
                className={
                  canDelete
                    ? "text-sm font-medium"
                    : "text-sm font-medium text-coval-text-muted"
                }
              >
                Delete organization
              </p>
              <p className="mt-1 text-sm text-coval-text-muted">
                Permanently delete this organization for every member.
              </p>
            </div>
            <Button
              variant="danger"
              disabled={busy || !canDelete || !setActive}
              onClick={() => {
                setAction("delete")
                setConfirmation("")
              }}
            >
              Delete organization
            </Button>
          </div>
          {!canDelete && (
            <OrganizationPermissionNotice
              reason={
                organization.adminDeleteEnabled
                  ? "Your role cannot delete this organization. Ask an admin to review this request."
                  : "Organization deletion is disabled by policy. Ask an admin about the available options."
              }
              request="organization deletion"
              disabled={busy}
            />
          )}
        </CardContent>
      </Card>
      {action && (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open && !busy) {
              setAction(null)
              setConfirmation("")
            }
          }}
        >
          <DialogContent size="md">
            <DialogHeader>
              <DialogTitle>
                {action === "delete" ? "Delete" : "Leave"} {organization.name}?
              </DialogTitle>
              <DialogDescription>
                {action === "delete"
                  ? "This permanently deletes the organization for everyone. This cannot be undone."
                  : "You’ll lose access to this organization and all of its workspaces."}
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="space-y-4">
              <div className="flex gap-3 rounded-md border border-coval-danger-border bg-coval-danger-surface p-4 text-sm text-coval-danger-on-surface">
                <AlertTriangle className="size-4 shrink-0" />
                <p>
                  {action === "delete"
                    ? "All members will lose access to this organization. This action cannot be undone."
                    : "Organizations must keep at least one admin. If you’re the only admin, promote another member before leaving."}
                </p>
              </div>
              <Label htmlFor="organization-removal">
                Type {organization.name} to confirm
              </Label>
              <Input
                id="organization-removal"
                value={confirmation}
                disabled={busy}
                autoComplete="off"
                onChange={(event) => setConfirmation(event.target.value)}
              />
            </DialogBody>
            <DialogFooter>
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  setAction(null)
                  setConfirmation("")
                }}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={busy}
                disabled={
                  busy ||
                  confirmation !== organization.name ||
                  !scoped ||
                  !setActive ||
                  (action === "delete" && !canDelete)
                }
                onClick={() => void remove()}
              >
                {action === "delete"
                  ? "Delete organization"
                  : "Leave organization"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
